import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { HelpButton } from "@/components/help/HelpButton";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate, formatDateTime, prettify } from "@/lib/format";
import { ControlBadge } from "@/components/ControlBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  UserPlus, AlarmClock, MessageSquare, CalendarCheck2, Briefcase, DollarSign, Map as MapIcon,
  Building2, Search, Sparkles, HelpCircle, FileText, Handshake, BarChart3, LifeBuoy,
  ArrowRight, ChevronRight, Filter, RotateCcw, Wallet, TrendingUp, Info,
  BrainCircuit, RefreshCw, ShieldCheck, AlertTriangle, ListChecks, Clock, Mail, Settings2, Layers,
  MessagesSquare, GitBranch, Route as RouteIcon, Gauge, Pin,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, LineChart, Line,
} from "recharts";

const NAVY = "#1C4358", RED = "#9C1E22", GREEN = "#2F7D5A", AMBER = "#B7791F", SKY = "#2C6480", KHAKI = "#5A5E2E";
const TONES = {
  navy: { chip: "rgba(28,67,88,0.10)", color: NAVY },
  sky: { chip: "rgba(155,185,206,0.24)", color: SKY },
  green: { chip: "rgba(47,125,90,0.12)", color: GREEN },
  red: { chip: "rgba(156,30,34,0.10)", color: RED },
  amber: { chip: "rgba(183,121,31,0.14)", color: AMBER },
  khaki: { chip: "rgba(196,200,155,0.30)", color: KHAKI },
};

const leadName = (l) => l?.name || [l?.first_name, l?.last_name].filter(Boolean).join(" ") || l?.contact_name || "New lead";

const SectionCard = ({ children, className = "", testid }) => (
  <Card className={`p-4 sm:p-5 border-[#E6EDF2] ${className}`} style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    {children}
  </Card>
);

const SectionHead = ({ icon: Icon, title, count, tone = "navy", badge, hint, action }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: t.chip }}>
          <Icon size={16} style={{ color: t.color }} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-sm font-bold text-[#252221] truncate">{title}</h3>
            {badge && <ControlBadge kind={badge} />}
            {typeof count === "number" && (
              <span className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full"
                style={{ backgroundColor: count > 0 ? t.chip : "rgba(75,85,99,0.08)", color: count > 0 ? t.color : "#6B7280" }}>
                {count}
              </span>
            )}
          </div>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
      {action}
    </div>
  );
};

// PROMPT 12H — Command Center digest quick action (uses the existing 12G send-now endpoint; staff-scoped)
const CcDigestButton = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ext-digest/send-now");
      if (data.sent) toast.success("Your external referral digest has been queued.");
      else toast.info("No external referral items need attention right now.");
    } catch {
      toast.error("Could not send your digest. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={send} disabled={busy} data-testid="cc-extref-digest-btn"
      className="h-8 shrink-0 border-[#D6E0E7] text-[#1C4358] gap-1 text-[12px]">
      {busy ? <Icons.Loader2 size={13} className="animate-spin" /> : <Icons.Mail size={13} />} Email me my digest
    </Button>
  );
};


const Kpi = ({ icon: Icon, label, value, sub, tone = "navy", onClick, testid, disabled }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <Card onClick={disabled ? undefined : onClick}
      className={`p-4 border-[#E6EDF2] transition-shadow ${onClick && !disabled ? "cursor-pointer hover:shadow-[var(--mmm-shadow-md)]" : ""} ${disabled ? "opacity-70" : ""}`}
      style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: t.chip }}>
          <Icon size={18} style={{ color: t.color }} />
        </div>
        {disabled && <span className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground">Soon</span>}
      </div>
      <div className="text-2xl font-bold tabular-nums font-display text-[#252221] leading-none mt-2.5">{value}</div>
      <div className="text-xs text-muted-foreground mt-1.5 leading-tight">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
};

const EmptyRow = ({ text }) => <p className="text-sm text-muted-foreground py-5 text-center">{text}</p>;

const RowLink = ({ onClick, primary, secondary, right, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[rgba(155,185,206,0.08)] -mx-2 px-2 rounded-lg">
    <div className="min-w-0">
      <div className="text-sm font-medium text-[#252221] truncate">{primary}</div>
      {secondary && <div className="text-xs text-muted-foreground truncate">{secondary}</div>}
    </div>
    {right || <ChevronRight size={15} className="text-muted-foreground shrink-0" />}
  </button>
);

// Filter select that maps "all" sentinel -> empty
const FSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testid} className="h-9 w-full sm:w-[168px] rounded-lg text-xs font-medium bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const EMPTY_FILTERS = { territory: "", country: "", state: "", city: "", service_lane: "", assigned_user_id: "", source: "" };

const briefTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const nextTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const PrefRow = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="text-sm text-[#252221]">{label}</div>
      {hint && <div className="text-[11px] text-[#B7791F]">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

// PROMPT 3A — Admin-only AI Daily Briefing. Sits ON TOP of the rule-based suggestions
// (which always remain below as the fallback). Self-contained fetch so a slow LLM call
// never blocks the rest of the Command Center.
const AIBriefing = () => {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [meta, setMeta] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const fetchBriefing = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/command-center/briefing");
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, error: true });
    }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/command-center/briefing/preferences");
      setPrefs(data.preferences);
      setMeta(data);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { fetchBriefing(); fetchPrefs(); }, [fetchBriefing, fetchPrefs]);

  const toggle = async (on) => {
    try {
      await api.put("/settings", { ai_briefing_enabled: on });
      toast.success(on ? "AI Daily Briefing turned on" : "AI Daily Briefing turned off");
      if (on) fetchBriefing();
      else setState({ loading: false, enabled: false });
    } catch { toast.error("Couldn't update that setting."); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/command-center/briefing/regenerate");
      setState({ loading: false, ...data });
      toast.success("Fresh briefing generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate a briefing right now.");
    } finally { setBusy(false); }
  };

  const savePref = async (patch) => {
    setPrefs((p) => ({ ...(p || {}), ...patch }));
    try {
      const { data } = await api.put("/command-center/briefing/preferences", patch);
      setPrefs(data.preferences);
      setMeta((m) => ({ ...(m || {}), next_run: data.next_run, timezone_warning: data.timezone_warning }));
      if (data.warning) toast.warning(data.warning); else toast.success("Briefing settings saved");
    } catch { toast.error("Couldn't save settings."); fetchPrefs(); }
  };

  const emailMe = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/command-center/briefing/email");
      toast.success(`Briefing emailed to ${data.emailed_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't email the briefing.");
    } finally { setEmailing(false); }
  };

  const b = state.briefing;
  const c = b?.content;
  const enabled = state.enabled !== false;

  return (
    <SectionCard testid="ai-briefing-card" className="border-l-4 border-l-[#1C4358]">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}>
            <BrainCircuit size={18} style={{ color: NAVY }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-base font-bold text-[#252221]">AI Daily Briefing</h3>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(28,67,88,0.10)", color: NAVY }}>
                <ShieldCheck size={11} /> Admin only
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">A plain-language summary of your day, written for you.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer">
            <span>AI</span>
            <Switch checked={enabled} onCheckedChange={toggle} data-testid="ai-briefing-toggle" />
          </label>
          {enabled && (
            <>
              <Button variant="outline" size="sm" onClick={emailMe} disabled={emailing} data-testid="ai-briefing-email-me"
                className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
                <Mail size={14} className="mr-1" /> {emailing ? "Sending…" : "Email me"}
              </Button>
              <Button variant="outline" size="sm" onClick={regenerate} disabled={busy} data-testid="ai-briefing-regenerate"
                className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
                <RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> {busy ? "Generating…" : "Regenerate"}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowSettings((v) => !v)} data-testid="ai-briefing-settings-toggle"
                className="h-8 w-8 text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
                <Settings2 size={16} />
              </Button>
            </>
          )}
        </div>
      </div>

      {enabled && prefs && (
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-muted-foreground mb-3 -mt-1" data-testid="ai-briefing-status">
          <span className="inline-flex items-center gap-1"><RefreshCw size={12} /> Auto-refresh: <strong className="text-[#252221]">{prefs.auto_generate ? "On" : "Off"}</strong></span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> Next: {prefs.auto_generate ? nextTime(meta?.next_run) : "—"}</span>
          <span className="inline-flex items-center gap-1"><Mail size={12} /> Email: <strong className="text-[#252221]">{prefs.email_enabled ? "On" : "Off"}</strong></span>
          {meta?.timezone_warning && (
            <span className="inline-flex items-center gap-1 text-[#B7791F]" data-testid="ai-briefing-tz-warning"><AlertTriangle size={12} /> Timezone not set — using UTC</span>
          )}
        </div>
      )}

      {enabled && showSettings && prefs && (
        <div className="rounded-lg border border-[#EEF3F6] p-3.5 mb-3 space-y-3" data-testid="ai-briefing-settings">
          <div className="text-xs font-bold uppercase tracking-wide text-[#1C4358]/80">Briefing settings</div>
          <PrefRow label="Auto-generate each morning">
            <Switch checked={!!prefs.auto_generate} onCheckedChange={(v) => savePref({ auto_generate: v })} data-testid="pref-auto-generate" />
          </PrefRow>
          <PrefRow label="Email me my briefing" hint={!meta?.email_configured ? "Email provider not configured" : null}>
            <Switch checked={!!prefs.email_enabled} disabled={!meta?.email_configured} onCheckedChange={(v) => savePref({ email_enabled: v })} data-testid="pref-email-enabled" />
          </PrefRow>
          <PrefRow label="Send only when there are important updates">
            <Switch checked={!!prefs.only_important} onCheckedChange={(v) => savePref({ only_important: v })} data-testid="pref-only-important" />
          </PrefRow>
          <PrefRow label="Preferred delivery time">
            <input type="time" value={prefs.delivery_time || "06:00"} onChange={(e) => savePref({ delivery_time: e.target.value })}
              data-testid="pref-delivery-time" className="h-8 rounded-md border border-[#D6E0E7] px-2 text-sm bg-white" />
          </PrefRow>
          <PrefRow label="Territory scope">
            <span className="text-sm text-[#252221]">{meta?.scope_label || "—"}</span>
          </PrefRow>
        </div>
      )}

      {!enabled ? (
        <div className="rounded-lg bg-[rgba(155,185,206,0.08)] px-4 py-3 text-sm text-muted-foreground" data-testid="ai-briefing-off">
          AI Daily Briefing is turned off. You're seeing the rule-based suggestions below. Flip the switch to turn it back on.
        </div>
      ) : state.loading || busy ? (
        <div className="space-y-2" data-testid="ai-briefing-loading">
          <p className="text-sm text-muted-foreground">Preparing today's briefing… this can take a few seconds.</p>
          <Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/2" />
        </div>
      ) : state.available === false || !c ? (
        <div className="rounded-lg bg-[rgba(183,121,31,0.06)] px-4 py-3" data-testid="ai-briefing-unavailable">
          <p className="text-sm text-[#252221]">We couldn't generate an AI briefing right now — no problem, your rule-based suggestions are ready below.</p>
          <Button variant="outline" size="sm" onClick={regenerate} disabled={busy} className="mt-2 border-[#D6E0E7] text-[#1C4358]">
            <RefreshCw size={14} className="mr-1" /> Try again
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-base font-semibold text-[#252221]" data-testid="ai-briefing-headline">{c.headline}</p>
            {c.summary && <p className="text-sm text-[#3F3A38] mt-1" data-testid="ai-briefing-summary">{c.summary}</p>}
          </div>

          {(c.sections || []).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {c.sections.map((sec, i) => (
                <div key={i} className="rounded-lg border border-[#EEF3F6] px-3 py-2.5" data-testid={`ai-briefing-section-${i}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#1C4358]/80">{sec.title}</div>
                  <div className="text-sm text-[#252221] mt-1">{sec.body}</div>
                </div>
              ))}
            </div>
          )}

          {(c.top_actions || []).length > 0 && (
            <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(47,125,90,0.06)" }}>
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2" style={{ color: GREEN }}>
                <ListChecks size={14} /> Top 3 actions
              </div>
              <ol className="space-y-1.5">
                {c.top_actions.map((a, i) => (
                  <li key={i} className="flex gap-2.5 text-sm" data-testid={`ai-briefing-action-${i}`}>
                    <span className="shrink-0 h-5 w-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center" style={{ backgroundColor: GREEN }}>{i + 1}</span>
                    <span className="text-[#252221]"><strong>{a.action}</strong>{a.why ? <span className="text-muted-foreground"> — {a.why}</span> : null}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(c.warnings || []).length > 0 && (
            <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(183,121,31,0.08)" }}>
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: AMBER }}>
                <AlertTriangle size={14} /> Worth a look
              </div>
              <ul className="space-y-1 list-disc pl-5">
                {c.warnings.map((w, i) => <li key={i} className="text-sm text-[#252221]" data-testid={`ai-briefing-warning-${i}`}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap pt-1 border-t border-[#F0F4F7]">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="ai-briefing-timestamp">
              <Clock size={12} /> Last generated {briefTime(b.generated_at)}{b.generated_by_name ? ` · by ${b.generated_by_name}` : ""}
            </span>
            <span className="text-[11px] text-muted-foreground italic">Summaries only — review and confirm before acting. Not legal, medical, financial, or care-placement advice.</span>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

// PROMPT 4A — Lead Source Analytics widget. Reuses the Command Center filters (passed in)
// + its own date range. Purely rule-based insights; LLM insights can be layered later.
const CTA_COLORS = {
  "Free Consultation": NAVY, "Start Live Chat": SKY, "Service Cards": KHAKI,
  "Contact Form": GREEN, "Final CTA": RED, Other: "#9AA5B1",
};
const LANE_COLORS = [NAVY, SKY, GREEN, AMBER, RED, KHAKI, "#9AA5B1"];
const INSIGHT_TONE = { green: GREEN, sky: SKY, red: RED, navy: NAVY, amber: AMBER, khaki: KHAKI };

const MiniStat = ({ label, value, tone = "navy", testid }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <div className="rounded-xl border border-[#EEF3F6] px-3 py-3" style={{ backgroundColor: t.chip }} data-testid={testid}>
      <div className="text-2xl font-bold tabular-nums font-display leading-none" style={{ color: t.color }}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{label}</div>
    </div>
  );
};

const BreakdownList = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">{title}</div>
    {(!items || items.length === 0) ? (
      <p className="text-xs text-muted-foreground py-2">No data yet.</p>
    ) : (
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-[#252221] truncate">{it.name}</span>
            <span className="tabular-nums font-semibold text-[#1C4358]">{it.value}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// PROMPT 8 (Phase 8B) — CRM pipeline health: clickable stat cards → filtered CRM views.
const ClickStat = ({ label, value, tone = "navy", onClick, testid }) => {
  const t = TONES[tone] || TONES.navy;
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="text-left rounded-xl border border-[#EEF3F6] px-3 py-3 transition-all hover:-translate-y-0.5 hover:shadow-sm"
      style={{ backgroundColor: t.chip }}>
      <div className="text-2xl font-bold tabular-nums font-display leading-none" style={{ color: t.color }}>{value}</div>
      <div className="text-[11px] text-[#4B5563] mt-1 leading-tight flex items-center gap-1">{label}<ArrowRight size={11} className="opacity-50" /></div>
    </button>
  );
};

const PipelineHealth = ({ ph }) => {
  const navigate = useNavigate();
  if (!ph || ph.available === false) return null;
  const cards = [
    { label: "Needs Review", value: ph.needs_review ?? 0, tone: "navy", to: "/crm/pipeline", tid: "ph-needs-review" },
    { label: "Urgent (open)", value: ph.urgent ?? 0, tone: "red", to: "/crm/pipeline", tid: "ph-urgent" },
    { label: "Overdue Follow-Ups", value: ph.overdue_follow_ups ?? 0, tone: "red", to: "/crm/follow-ups?status=overdue", tid: "ph-overdue" },
    { label: "Due Today", value: ph.due_today ?? 0, tone: "amber", to: "/crm/follow-ups?status=due_today", tid: "ph-due-today" },
    { label: "Active Deals", value: ph.opportunities_active ?? 0, tone: "sky", to: "/crm/opportunities", tid: "ph-active-deals" },
    { label: "Revenue Pending", value: ph.revenue_pending ?? 0, tone: "amber", to: "/crm/pipeline", tid: "ph-revenue-pending" },
    { label: "Contract Pending", value: ph.contract_pending ?? 0, tone: "sky", to: "/crm/pipeline", tid: "ph-contract-pending" },
    { label: "Closed Won (mo.)", value: ph.closed_won_month ?? 0, tone: "green", to: "/crm/reports", tid: "ph-closed-won" },
  ];
  return (
    <SectionCard testid="section-pipeline-health">
      <SectionHead icon={RouteIcon} title="CRM Pipeline Health" tone="navy"
        hint="Live pipeline signals — click a card to jump into the matching CRM view" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {cards.map((c) => (
          <ClickStat key={c.tid} label={c.label} value={c.value} tone={c.tone} testid={c.tid} onClick={() => navigate(c.to)} />
        ))}
      </div>
    </SectionCard>
  );
};

const LeadSourceAnalytics = ({ filters }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const merged = { ...filters, date_from: range.from, date_to: range.to };
      const params = Object.fromEntries(Object.entries(merged).filter(([, v]) => v));
      const { data } = await api.get("/command-center/lead-analytics", { params });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, range]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const hasAny = (t.all_public || 0) > 0;

  return (
    <SectionCard testid="lead-analytics-widget">
      <SectionHead icon={BarChart3} title="Lead Source Analytics" tone="navy"
        hint="Which homepage CTAs, pages and areas are producing family inquiries"
        action={
          <div className="flex items-center gap-1.5">
            <input type="date" data-testid="lead-analytics-date-from" value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs text-[#252221] bg-white" aria-label="From date" />
            <span className="text-xs text-muted-foreground">to</span>
            <input type="date" data-testid="lead-analytics-date-to" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs text-[#252221] bg-white" aria-label="To date" />
            {(range.from || range.to) && (
              <Button variant="ghost" size="sm" data-testid="lead-analytics-date-reset"
                onClick={() => setRange({ from: "", to: "" })} className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)] px-2">
                <RotateCcw size={13} />
              </Button>
            )}
          </div>
        } />

      {loading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : !hasAny ? (
        <div className="text-center py-10" data-testid="lead-analytics-empty">
          <MessageSquare size={26} className="mx-auto text-[#9BB9CE]" />
          <p className="mt-3 text-sm text-[#252221]">No public website leads yet in your scope.</p>
          <p className="text-xs text-muted-foreground mt-1">As families submit the homepage forms, their source, CTA and location will appear here.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Count cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="In current view" value={t.total ?? 0} tone="navy" testid="lead-analytics-total" />
            <MiniStat label="Today" value={t.today ?? 0} tone="green" testid="lead-analytics-today" />
            <MiniStat label="This week" value={t.week ?? 0} tone="sky" testid="lead-analytics-week" />
            <MiniStat label="This month" value={t.month ?? 0} tone="khaki" testid="lead-analytics-month" />
          </div>

          {/* Insights */}
          {(data.insights || []).length > 0 && (
            <div className="rounded-lg p-3 space-y-1.5" style={{ backgroundColor: "rgba(28,67,88,0.04)" }} data-testid="lead-analytics-insights">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#1C4358]/80">
                <Sparkles size={13} /> Insights
              </div>
              {data.insights.map((ins, i) => (
                <div key={i} className="flex items-start gap-2 text-sm" data-testid={`lead-analytics-insight-${i}`}>
                  <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: INSIGHT_TONE[ins.tone] || NAVY }} />
                  <span className="text-[#252221]">{ins.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Charts: CTA pie + service lane bar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div data-testid="lead-analytics-cta-chart">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Leads by CTA / source</div>
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={(data.by_cta || []).filter((d) => d.value > 0)} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={80} innerRadius={44} paddingAngle={2}>
                    {(data.by_cta || []).filter((d) => d.value > 0).map((d) => (
                      <Cell key={d.name} fill={CTA_COLORS[d.name] || "#9AA5B1"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                {(data.by_cta || []).filter((d) => d.value > 0).map((d) => (
                  <span key={d.name} className="inline-flex items-center gap-1.5 text-[11px] text-[#252221]">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CTA_COLORS[d.name] || "#9AA5B1" }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>

            <div data-testid="lead-analytics-service-chart">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Leads by service lane</div>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={data.by_service || []} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid horizontal={false} stroke="#EEF3F6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {(data.by_service || []).map((_, i) => <Cell key={i} fill={LANE_COLORS[i % LANE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Trend */}
          <div data-testid="lead-analytics-trend">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Last 14 days</div>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={data.trend || []} margin={{ left: -18, right: 8 }}>
                <CartesianGrid vertical={false} stroke="#EEF3F6" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke={NAVY} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown lists */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pt-1 border-t border-[#F0F4F7]">
            <BreakdownList title="By urgency" items={data.by_urgency} testid="lead-analytics-by-urgency" />
            <BreakdownList title="By page" items={data.by_page} testid="lead-analytics-by-page" />
            <BreakdownList title="By city / state" items={data.by_location} testid="lead-analytics-by-location" />
            <BreakdownList title="By territory / franchise" items={data.by_territory} testid="lead-analytics-by-territory" />
          </div>

          {/* PROMPT 5A — Service Lane Performance */}
          {data.by_service_lane && (
            <div className="pt-5 border-t border-[#F0F4F7] space-y-5" data-testid="service-lane-performance">
              <div className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-[#1C4358]">
                <Layers size={14} /> Service Lane Performance
              </div>

              {(data.lane_insights || []).length > 0 && (
                <div className="rounded-lg p-3 space-y-1.5" style={{ backgroundColor: "rgba(47,125,90,0.06)" }} data-testid="service-lane-insights">
                  {data.lane_insights.map((ins, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm" data-testid={`lane-insight-${i}`}>
                      <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: INSIGHT_TONE[ins.tone] || NAVY }} />
                      <span className="text-[#252221]">{ins.text}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div data-testid="service-lane-bar">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Leads by service lane</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.by_service_lane} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid horizontal={false} stroke="#EEF3F6" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                        {data.by_service_lane.map((_, i) => <Cell key={i} fill={LANE_COLORS[i % LANE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div data-testid="service-lane-pie">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Service lane share</div>
                  {data.by_service_lane.some((d) => d.total > 0) ? (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={data.by_service_lane.filter((d) => d.total > 0)} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={78} innerRadius={42} paddingAngle={2}>
                            {data.by_service_lane.filter((d) => d.total > 0).map((_, i) => <Cell key={i} fill={LANE_COLORS[i % LANE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                        {data.by_service_lane.filter((d) => d.total > 0).map((d, i) => (
                          <span key={d.slug} className="inline-flex items-center gap-1.5 text-[11px] text-[#252221]">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: LANE_COLORS[data.by_service_lane.indexOf(d) % LANE_COLORS.length] }} />
                            {d.name} ({d.total})
                          </span>
                        ))}
                      </div>
                    </>
                  ) : <p className="text-xs text-muted-foreground py-8 text-center">No service lane leads yet.</p>}
                </div>
              </div>

              {/* Lane count table */}
              <div className="overflow-x-auto" data-testid="service-lane-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-[#1C4358]/60 border-b border-[#EEF3F6]">
                      <th className="py-2 pr-2 font-bold">Service lane</th>
                      <th className="py-2 px-2 font-bold text-right">Today</th>
                      <th className="py-2 px-2 font-bold text-right">Week</th>
                      <th className="py-2 px-2 font-bold text-right">Month</th>
                      <th className="py-2 pl-2 font-bold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_service_lane.map((l) => (
                      <tr key={l.slug} className="border-b border-[#F5F8FA]" data-testid={`lane-stat-${l.slug}`}>
                        <td className="py-2 pr-2 text-[#252221]">{l.name}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{l.today}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{l.week}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{l.month}</td>
                        <td className="py-2 pl-2 text-right tabular-nums font-semibold text-[#1C4358]">{l.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Territory table */}
              {(data.service_lane_territory || []).length > 0 && (
                <div data-testid="service-lane-territory">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Service lane leads by territory / city</div>
                  <ul className="space-y-1.5">
                    {data.service_lane_territory.map((r, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-[#252221] truncate"><span className="font-medium text-[#1C4358]">{r.lane}</span> · {r.territory}</span>
                        <span className="tabular-nums font-semibold text-[#1C4358]">{r.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" size="sm" data-testid="lead-analytics-open-leads" onClick={() => navigate("/leads")}
              className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
              View all leads <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

// PROMPT 7A — Conversations & Conversion widget. Ties live chat + conditional intake +
// consultation requests + CRM lead movement together. Rule-based insights only.
const CONV_SOURCE_COLORS = { "Live Chat": SKY, "Intake Form": GREEN, Other: "#9AA5B1" };
const FUNNEL_COLORS = [NAVY, SKY, GREEN, AMBER, RED, KHAKI];

const ConvTable = ({ title, items, testid, cols = ["consults", "rate"] }) => (
  <div data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">{title}</div>
    {(!items || items.length === 0) ? <p className="text-xs text-muted-foreground py-2">No data yet.</p> : (
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wide text-[#1C4358]/60 border-b border-[#EEF3F6]">
          <th className="text-left py-1.5 font-bold">Name</th>
          <th className="text-right py-1.5 font-bold px-2">Leads</th>
          {cols.includes("consults") && <th className="text-right py-1.5 font-bold px-2">Consult req.</th>}
          {cols.includes("rate") && <th className="text-right py-1.5 font-bold">Rate</th>}
        </tr></thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={i} className="border-b border-[#F5F8FA]">
              <td className="py-1.5 pr-2 text-[#252221] truncate max-w-[160px]" title={r.name}>{r.name}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{r.leads}</td>
              {cols.includes("consults") && <td className="py-1.5 px-2 text-right tabular-nums">{r.consults}</td>}
              {cols.includes("rate") && <td className="py-1.5 text-right tabular-nums font-semibold" style={{ color: r.rate >= 50 ? GREEN : NAVY }}>{r.rate}%</td>}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Funnel = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="space-y-1.5" data-testid="conversion-funnel">
      {items.map((s, i) => (
        <div key={s.stage} className="flex items-center gap-2" data-testid={`funnel-${i}`}>
          <span className="text-[11px] text-[#4B5563] w-40 shrink-0 truncate text-right">{s.stage}</span>
          <div className="flex-1 h-7 rounded-md bg-[#F0F4F7] overflow-hidden">
            <div className="h-full rounded-md flex items-center justify-end px-2 text-[11px] font-bold text-white transition-all"
              style={{ width: `${Math.max(6, (s.value / max) * 100)}%`, backgroundColor: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }}>
              {s.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ConversationsConversion = ({ filters }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: "", to: "" });
  const [wf, setWf] = useState({ reverse_topic: "", urgency: "", ab_variation: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const merged = { ...filters, date_from: range.from, date_to: range.to, ...wf };
      const params = Object.fromEntries(Object.entries(merged).filter(([, v]) => v));
      const { data } = await api.get("/command-center/conversations-conversion", { params });
      setData(data);
    } catch { setData(null); } finally { setLoading(false); }
  }, [filters, range, wf]);
  useEffect(() => { load(); }, [load]);

  const c = data?.conversations || {};
  const it = data?.intake || {};
  const cv = data?.conversion || {};
  const wfilters = data?.widget_filters || {};

  return (
    <SectionCard testid="conversations-conversion-widget">
      <SectionHead icon={MessagesSquare} title="Conversations & Conversion" tone="sky"
        hint="How live chat + intake forms turn family problems into leads and consultations"
        action={
          <div className="flex items-center gap-1.5">
            <input type="date" data-testid="cc-conv-date-from" value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs text-[#252221] bg-white" aria-label="From date" />
            <span className="text-xs text-muted-foreground">to</span>
            <input type="date" data-testid="cc-conv-date-to" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs text-[#252221] bg-white" aria-label="To date" />
            {(range.from || range.to) && (
              <Button variant="ghost" size="sm" data-testid="cc-conv-date-reset" onClick={() => setRange({ from: "", to: "" })}
                className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)] px-2"><RotateCcw size={13} /></Button>
            )}
          </div>
        } />

      {/* widget-local filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <FSelect testid="cc-conv-reverse" value={wf.reverse_topic} onChange={(v) => setWf((f) => ({ ...f, reverse_topic: v }))}
          placeholder="All reverse-search pages" options={(wfilters.reverse_topics || []).map((r) => ({ value: r.value, label: r.label }))} />
        <FSelect testid="cc-conv-urgency" value={wf.urgency} onChange={(v) => setWf((f) => ({ ...f, urgency: v }))}
          placeholder="All urgency" options={(wfilters.urgencies || []).map((u) => ({ value: u, label: prettify(u) }))} />
        <FSelect testid="cc-conv-ab" value={wf.ab_variation} onChange={(v) => setWf((f) => ({ ...f, ab_variation: v }))}
          placeholder="All A/B variations" options={(wfilters.ab_variations || []).map((a) => ({ value: a, label: `Variation ${String.fromCharCode(65 + Number(a))}` }))} />
        {(wf.reverse_topic || wf.urgency || wf.ab_variation) && (
          <Button variant="ghost" size="sm" data-testid="cc-conv-filter-reset" onClick={() => setWf({ reverse_topic: "", urgency: "", ab_variation: "" })}
            className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]"><RotateCcw size={13} className="mr-1" /> Clear</Button>
        )}
      </div>

      {loading && !data ? <Skeleton className="h-72 w-full" /> : !data?.has_any ? (
        <div className="text-center py-10" data-testid="conversations-conversion-empty">
          <MessagesSquare size={26} className="mx-auto text-[#9BB9CE]" />
          <p className="mt-3 text-sm text-[#252221]">No conversations or intakes in your scope yet.</p>
          <p className="text-xs text-muted-foreground mt-1">As live chats start and forms are completed, this widget fills in automatically.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Conversation metrics */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><MessagesSquare size={13} /> Live chat</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MiniStat label="Chats (in view)" value={c.total ?? 0} tone="sky" testid="cc-conv-chat-total" />
              <MiniStat label="Waiting for human" value={c.waiting_human ?? 0} tone="amber" testid="cc-conv-chat-waiting" />
              <MiniStat label="Resolved" value={c.resolved ?? 0} tone="green" testid="cc-conv-chat-resolved" />
              <MiniStat label="Missed / after-hours" value={c.missed_offline ?? 0} tone="red" testid="cc-conv-chat-missed" />
            </div>
          </div>

          {/* Intake metrics */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><ListChecks size={13} /> Intake forms</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <MiniStat label="Views" value={it.views ?? 0} tone="navy" testid="cc-conv-form-views" />
              <MiniStat label="Starts" value={it.starts ?? 0} tone="sky" testid="cc-conv-form-starts" />
              <MiniStat label="Completions" value={it.completions ?? 0} tone="green" testid="cc-conv-form-completions" />
              <MiniStat label="Abandon rate" value={`${it.abandon_rate ?? 0}%`} tone="amber" testid="cc-conv-form-abandon" />
              <MiniStat label="AI thank-you" value={it.ai_thank_you_generated ?? 0} tone="khaki" testid="cc-conv-form-aity" />
            </div>
          </div>

          {/* Insights */}
          {(data.insights || []).length > 0 && (
            <div className="rounded-lg p-3 space-y-1.5" style={{ backgroundColor: "rgba(44,100,128,0.06)" }} data-testid="cc-conv-insights">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#1C4358]/80"><Sparkles size={13} /> Insights</div>
              {data.insights.map((ins, i) => (
                <div key={i} className="flex items-start gap-2 text-sm" data-testid={`cc-conv-insight-${i}`}>
                  <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: INSIGHT_TONE[ins.tone] || NAVY }} />
                  <span className="text-[#252221]">{ins.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Funnel */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><RouteIcon size={13} /> Conversion funnel</div>
            <Funnel items={cv.funnel || []} />
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span>Consultation requested: <strong className="text-[#1C4358]">{cv.consultation_requested ?? 0}</strong></span>
              <span>{cv.booked_available ? <>Booked consultations: <strong className="text-[#1C4358]">{cv.consultation_booked ?? 0}</strong></> : "Booked-consultation data not connected yet."}</span>
            </div>
          </div>

          {/* Charts: conversions by lane (grouped bar) + sources pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div data-testid="cc-conv-lane-chart">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Conversions by service lane</div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.conv_by_lane || []} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid horizontal={false} stroke="#EEF3F6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="leads" name="Leads" fill={NAVY} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="consults" name="Consult req." fill={GREEN} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div data-testid="cc-conv-source-pie">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Lead sources (chat vs form)</div>
              {(data.sources_pie || []).some((d) => d.value > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={210}>
                    <PieChart>
                      <Pie data={(data.sources_pie || []).filter((d) => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={44} paddingAngle={2}>
                        {(data.sources_pie || []).filter((d) => d.value > 0).map((d) => <Cell key={d.name} fill={CONV_SOURCE_COLORS[d.name] || "#9AA5B1"} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                    {(data.sources_pie || []).filter((d) => d.value > 0).map((d) => (
                      <span key={d.name} className="inline-flex items-center gap-1.5 text-[11px] text-[#252221]">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CONV_SOURCE_COLORS[d.name] || "#9AA5B1" }} />{d.name} ({d.value})
                      </span>
                    ))}
                  </div>
                </>
              ) : <p className="text-xs text-muted-foreground py-8 text-center">No lead sources in this view yet.</p>}
            </div>
          </div>

          {/* Tables: top converting pages + cities + A/B */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 pt-1 border-t border-[#F0F4F7]">
            <ConvTable title="Top converting pages" items={data.conv_by_page} testid="cc-conv-by-page" />
            <ConvTable title="Top converting cities" items={data.conv_by_location} testid="cc-conv-by-location" />
            <ConvTable title="By A/B variation" items={data.conv_by_ab} testid="cc-conv-by-ab" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <ConvTable title="By reverse-search page" items={data.conv_by_reverse} testid="cc-conv-by-reverse" />
            <ConvTable title="By CTA" items={data.conv_by_cta} testid="cc-conv-by-cta" />
            <ConvTable title="By urgency" items={data.conv_by_urgency} testid="cc-conv-by-urgency" />
          </div>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" data-testid="cc-conv-open-chat" onClick={() => navigate("/chat")}
              className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
              Open Live Chat inbox <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

const SEO_STATUS = { queued: AMBER, sending: AMBER, sent: GREEN, failed: RED, skipped: "#4B5563" };
const seoScoreColor = (score, label) => (label === "Not Enough Data" ? "#4B5563" : score >= 85 ? GREEN : score >= 60 ? AMBER : RED);

const SeoHealthTile = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/seo-intelligence/command-center-tile")
      .then(({ data }) => setD(data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Skeleton className="h-[150px] w-full rounded-[0.9rem]" />;
  if (!d) return null;
  const col = seoScoreColor(d.score, d.label);
  const le = d.last_email;
  return (
    <Card data-testid="seo-health-tile" onClick={() => navigate(d.link || "/seo-intelligence")}
      className="p-5 border-[#E6EDF2] cursor-pointer hover:shadow-md hover:shadow-[#1C4358]/10 transition-shadow">
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center justify-center rounded-[0.9rem] px-4 py-3 shrink-0" style={{ backgroundColor: `${col}12` }}>
          <span className="font-display font-bold text-3xl" style={{ color: col }} data-testid="seo-tile-score">{d.score}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "#4B5563" }}>/ 100</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Gauge size={16} style={{ color: NAVY }} />
            <span className="font-display font-semibold text-[15px]" style={{ color: NAVY }}>SEO Health</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${col}1A`, color: col }} data-testid="seo-tile-label">{d.label}</span>
            <span className="text-[11px]" style={{ color: "#4B5563" }}>· {d.scope_label}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[12.5px]" style={{ color: "#252221" }}>
            <span data-testid="seo-tile-high">High-priority issues: <b style={{ color: d.high_issues ? RED : GREEN }}>{d.high_issues}</b></span>
            <span data-testid="seo-tile-needwork">Pages needing work: <b>{d.pages_needing_work}</b></span>
            <span>Pages audited: <b>{d.pages_total}</b></span>
          </div>
          {d.top_action && (
            <div className="text-[12.5px] mt-1.5 rounded-lg px-3 py-1.5" style={{ backgroundColor: "rgba(28,67,88,0.05)", color: NAVY }} data-testid="seo-tile-top-action">
              <b>Top action:</b> {d.top_action.title}{d.top_action.url ? ` (${d.top_action.url})` : ""}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-[11.5px] inline-flex items-center gap-1" style={{ color: le ? (SEO_STATUS[le.status] || "#4B5563") : "#4B5563" }} data-testid="seo-tile-last-email">
              <Mail size={12} /> {le ? `Last emailed ${formatDate(le.at)} to ${le.to} · ${le.status}` : "Not emailed yet"}
            </span>
            <span className="text-[12px] font-semibold inline-flex items-center gap-0.5" style={{ color: RED }}>Open dashboard <ArrowRight size={13} /></span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const ReportsShortcuts = () => {
  const navigate = useNavigate();
  const items = [
    { label: "Reporting Center", tab: "overview", icon: BarChart3 },
    { label: "Revenue pipeline", tab: "revenue", icon: TrendingUp },
    { label: "Money flow", tab: "money-flow", icon: TrendingUp },
    { label: "Partner referrals", tab: "partners", icon: Handshake },
    { label: "Follow-ups", tab: "follow-ups", icon: AlertTriangle },
    { label: "Territories", tab: "territories", icon: RouteIcon },
  ];
  return (
    <Card data-testid="reports-shortcuts" className="p-4 border-[#E6EDF2]">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} style={{ color: NAVY }} />
        <span className="font-display font-semibold text-[15px]" style={{ color: NAVY }}>Reports</span>
        <span className="text-[11px]" style={{ color: "#4B5563" }}>· aggregate, privacy-safe</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {items.map((it) => (
          <button key={it.tab} data-testid={`reports-shortcut-${it.tab}`} onClick={() => navigate(`/reports?tab=${it.tab}`)}
            className="flex flex-col items-center gap-1.5 rounded-[0.8rem] border px-2 py-3 hover:shadow-sm transition-shadow"
            style={{ borderColor: "#E6EDF2", color: NAVY }}>
            <it.icon size={17} />
            <span className="text-[11.5px] font-medium text-center leading-tight">{it.label}</span>
          </button>
        ))}
      </div>
    </Card>
  );
};


const PinnedReports = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/reports/center/pinned")
      .then(({ data }) => setD(data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Skeleton className="h-[120px] w-full rounded-[0.9rem]" />;
  if (!d || !(d.pinned || []).length) return null;
  const fmtMetric = (m) => (m.money ? formatCurrency(Number(m.value) || 0) : m.value);
  return (
    <Card data-testid="pinned-reports" className="p-4 border-[#E6EDF2]">
      <div className="flex items-center gap-2 mb-3">
        <Pin size={16} style={{ color: NAVY }} />
        <span className="font-display font-semibold text-[15px]" style={{ color: NAVY }}>Pinned Reports</span>
        <span className="text-[11px]" style={{ color: "#4B5563" }}>· {(d.pinned || []).length}/{d.max} · aggregate, privacy-safe</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {d.pinned.map((p) => (
          <button key={p.id} data-testid={`pinned-report-${p.id}`} onClick={() => navigate(`/reports?view=${p.id}`)}
            className="text-left rounded-[0.8rem] border p-3 hover:shadow-sm transition-shadow flex flex-col gap-1.5"
            style={{ borderColor: "#E6EDF2" }}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-display font-semibold text-[13.5px] truncate" style={{ color: NAVY }}>{p.name}</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "rgba(28,67,88,0.08)", color: NAVY }}>{p.category_label}</span>
            </div>
            {(p.metrics || []).length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[12.5px]" style={{ color: "#252221" }}>
                {p.metrics.map((m, i) => (
                  <span key={i}>{m.label}: <b style={{ color: NAVY }}>{fmtMetric(m)}</b></span>
                ))}
              </div>
            )}
            <div className="text-[11px] truncate" style={{ color: "#4B5563" }}>{p.filters_summary}</div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <span className="text-[10.5px]" style={{ color: "#8A9299" }}>{p.generated_at ? `Generated ${formatDateTime(p.generated_at)}` : "—"}</span>
              <span className="text-[11.5px] font-semibold inline-flex items-center gap-0.5" style={{ color: RED }}>Open report <ArrowRight size={12} /></span>
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
};


const SopSuggestionsNudge = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/help-assistant/suggestions/summary").then(({ data }) => setD(data)).catch(() => setD(null));
  }, [user]);
  if (!user?.is_admin || !d || d.total_to_review === 0) return null;
  return (
    <Card data-testid="sop-suggestions-nudge" className="p-4 border-[#E6EDF2]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icons.Lightbulb size={16} style={{ color: AMBER }} />
            <span className="font-display font-semibold text-[15px]" style={{ color: NAVY }}>Suggested for You</span>
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "rgba(183,121,31,0.12)", color: AMBER }}>{d.total_to_review} to review</span>
          </div>
          <div className="text-[12px] flex flex-wrap gap-x-3" style={{ color: "#4B5563" }}>
            {d.pending_suggestions > 0 && <span>{d.pending_suggestions} SOP suggestion{d.pending_suggestions > 1 ? "s" : ""}</span>}
            {d.ai_drafts > 0 && <span>{d.ai_drafts} AI draft{d.ai_drafts > 1 ? "s" : ""} awaiting review</span>}
          </div>
          {d.top && (
            <div className="mt-1.5 text-[13px]" style={{ color: "#252221" }}>
              Top suggestion: <b style={{ color: NAVY }}>“{d.top.title}”</b>
              {d.top.reason && <span className="text-[12px]" style={{ color: "#4B5563" }}> · {d.top.reason}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {d.ai_drafts > 0 && (
            <button data-testid="nudge-review-link" onClick={() => navigate("/sops/review")} className="text-[12.5px] font-semibold" style={{ color: NAVY }}>Review drafts</button>
          )}
          <button data-testid="nudge-suggestions-link" onClick={() => navigate("/sops/suggestions")} className="text-[12.5px] font-semibold inline-flex items-center gap-0.5" style={{ color: RED }}>
            Review suggestions <Icons.ArrowRight size={13} />
          </button>
        </div>
      </div>
    </Card>
  );
};


export default function CommandCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [controls, setControls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showControls, setShowControls] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [cc, reg] = await Promise.all([
        api.get("/command-center", { params }),
        controls ? Promise.resolve({ data: null }) : api.get("/controls-registry"),
      ]);
      setData(cc.data);
      if (reg.data) setControls(reg.data);
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const fo = data?.filter_options || {};

  if (loading && !data) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const s = data?.sections || {};
  const scope = data?.scope || {};
  const firstName = user?.full_name?.split(" ")[0] || "there";

  return (
    <div className="space-y-6" data-testid="command-center-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display text-2xl font-bold text-[#252221]">Command Center</h2>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(155,185,206,0.25)", color: NAVY }}>
              {scope.role_label}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Hi {firstName} — your whole operation at a glance. {scope.label}.</p>
        </div>
        <HelpButton route="/command-center" testid="cc-help-button" />
      </div>

      {/* Global/Local/Hybrid controls legend */}
      {controls && (
        <SectionCard testid="controls-legend">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Info size={16} className="text-[#1C4358]" />
              <span className="text-sm font-semibold text-[#252221]">Who controls what?</span>
              <span className="inline-flex items-center gap-1"><ControlBadge kind="global" /> <span className="text-xs text-muted-foreground">{controls.counts.global}</span></span>
              <span className="inline-flex items-center gap-1"><ControlBadge kind="local" /> <span className="text-xs text-muted-foreground">{controls.counts.local}</span></span>
              <span className="inline-flex items-center gap-1"><ControlBadge kind="hybrid" /> <span className="text-xs text-muted-foreground">{controls.counts.hybrid}</span></span>
            </div>
            <Button variant="outline" size="sm" data-testid="toggle-controls-list"
              className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]"
              onClick={() => setShowControls((v) => !v)}>
              {showControls ? "Hide" : "View"} all {controls.total} controls
            </Button>
          </div>
          {showControls && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="controls-list">
              {controls.controls.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-3 rounded-lg border border-[#EEF3F6] px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#252221]">{c.label}</div>
                    <div className="text-[11px] text-muted-foreground">{c.where} · {c.desc || c.section}</div>
                  </div>
                  <ControlBadge kind={c.kind} />
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Filter bar */}
      <SectionCard testid="command-center-filters" className="!p-3 sm:!p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1C4358] mr-1">
            <Filter size={14} /> Filters
            {activeFilterCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: RED }}>{activeFilterCount}</span>}
          </span>
          <FSelect testid="filter-territory" value={filters.territory} onChange={setF("territory")} placeholder="All territories"
            options={(fo.territories || []).map((t) => ({ value: t.id, label: t.name }))} />
          <FSelect testid="filter-country" value={filters.country} onChange={setF("country")} placeholder="All countries"
            options={(fo.countries || []).map((c) => ({ value: c, label: c }))} />
          <FSelect testid="filter-state" value={filters.state} onChange={setF("state")} placeholder="All states"
            options={(fo.states || []).map((c) => ({ value: c, label: c }))} />
          <FSelect testid="filter-city" value={filters.city} onChange={setF("city")} placeholder="All cities"
            options={(fo.cities || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-service-lane" value={filters.service_lane} onChange={setF("service_lane")} placeholder="All service lanes"
            options={(fo.service_lanes || []).map((l) => ({ value: l.value, label: l.label }))} />
          <FSelect testid="filter-assigned" value={filters.assigned_user_id} onChange={setF("assigned_user_id")} placeholder="Anyone assigned"
            options={(fo.staff || []).map((u) => ({ value: u.id, label: u.name }))} />
          <FSelect testid="filter-source" value={filters.source} onChange={setF("source")} placeholder="All sources"
            options={(fo.sources || []).map((c) => ({ value: c, label: prettify(c) }))} />
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" data-testid="filter-reset" onClick={() => setFilters(EMPTY_FILTERS)}
              className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]">
              <RotateCcw size={14} className="mr-1" /> Reset
            </Button>
          )}
        </div>
      </SectionCard>

      {/* KPI row: sections 1-6 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={UserPlus} label="Today's Leads" value={s.today_leads?.count ?? 0} tone="navy" testid="kpi-today-leads" onClick={() => navigate("/leads")} />
        <Kpi icon={AlarmClock} label="Urgent Follow-ups" value={s.urgent_followups?.count ?? 0} tone="red" testid="kpi-urgent" onClick={() => navigate("/tasks")} />
        <Kpi icon={MessageSquare} label="Live Chat Waiting" value={s.live_chat?.count ?? 0} sub={`${s.live_chat?.active ?? 0} active`} tone="sky" testid="kpi-live-chat" onClick={() => navigate("/chat")} />
        <Kpi icon={CalendarCheck2} label="Consultations Booked" value={s.consultations_booked?.count ?? 0} tone="green" testid="kpi-consults" onClick={() => navigate("/consultations")} />
        <Kpi icon={Briefcase} label="Open Deals" value={s.open_deals?.count ?? 0} sub={formatCurrency(s.open_deals?.value || 0)} tone="khaki" testid="kpi-open-deals" onClick={() => navigate("/revenue")} />
        <Kpi icon={DollarSign} label="Revenue (actual)" value={formatCurrency(s.revenue_flow?.actual || 0)} sub={`${formatCurrency(s.revenue_flow?.paid || 0)} collected`} tone="green" testid="kpi-revenue" onClick={() => navigate("/revenue")} />
      </div>

      {/* AI Daily Briefing (LLM) — sits above the rule-based suggestions, which remain as fallback */}
      <AIBriefing />

      {/* PROMPT 16-3 — Compact SEO Health tile (scoped, links to full SEO Intelligence dashboard) */}
      <SeoHealthTile />

      {/* PROMPT 17A — Reporting Center shortcuts */}
      <ReportsShortcuts />

      {/* PROMPT 17-1 — Pinned saved reports (owner-scoped, aggregate-first) */}
      <PinnedReports />

      {/* PROMPT 18-1 — SOP suggestions nudge (admin, scoped, safe metadata only) */}
      <SopSuggestionsNudge />

      {/* PROMPT 8 (Phase 8B) — CRM Pipeline Health (clickable → CRM views) */}
      <PipelineHealth ph={s.pipeline_health} />

      {/* PROMPT 10C — Meetings, rooms, documents & AI summaries */}
      {s.meetings?.available && (
        <SectionCard testid="section-meetings">
          <SectionHead icon={Icons.Video} title="Meetings & rooms" tone="navy" hint="Video meetings, documents, guest rooms & AI summaries — click into a filtered view" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {(s.meetings.cards || []).map((c) => (
              <button key={c.key} data-testid={`cc-meeting-${c.key}`} onClick={() => navigate(c.link)}
                className="text-left rounded-xl border border-[#EEF3F6] p-3 hover:border-[#1C4358]/40 transition-colors">
                <div className="text-2xl font-bold text-[#1C4358] tabular-nums">{c.count}</div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {/* PROMPT 11D — Client portal / free member activity */}
      {s.portal_activity?.available && (
        <SectionCard testid="section-portal-activity">
          <SectionHead icon={Icons.HeartHandshake} title="Family portal & free members" tone="red" hint="Conversions, portal activity, messages to answer & family engagement" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {[
              { key: "conversions", count: s.portal_activity.new_conversions?.count, label: "New free members (7d)", link: "/crm/leads" },
              { key: "guests", count: s.portal_activity.guests_to_convert?.count, label: "Guests ready to convert", link: "/meetings" },
              { key: "invites", count: s.portal_activity.invites_pending?.count, label: "Portal invites pending", link: "/crm/leads" },
              { key: "uploads", count: s.portal_activity.documents_uploaded?.count, label: "Client uploads (7d)", link: "/command-center" },
              { key: "checklist", count: s.portal_activity.checklist_completed?.count, label: "Checklist done (7d)", link: "/command-center" },
              { key: "messages", count: s.portal_activity.messages_needing_response?.count, label: "Messages to answer", link: "/crm/leads" },
              { key: "roadmaps", count: s.portal_activity.roadmaps_published?.count, label: "Roadmaps published (7d)", link: "/crm/leads" },
              { key: "active", count: s.portal_activity.active_this_week?.count, label: "Active this week", link: "/command-center" },
            ].map((c) => (
              <button key={c.key} data-testid={`cc-portal-${c.key}`} onClick={() => navigate(c.link)}
                className="text-left rounded-xl border border-[#EEF3F6] p-3 hover:border-[#9C1E22]/40 transition-colors">
                <div className="text-2xl font-bold text-[#9C1E22] tabular-nums">{c.count ?? 0}</div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {/* PROMPT 12B-1 — External referrals activity (partner/vendor/facility/professional) */}
      {s.external_referrals?.available && (
        <SectionCard testid="section-external-referrals">
          <SectionHead icon={Icons.Handshake} title="External referrals" tone="navy"
            hint="Partner / vendor / facility / professional referral activity & stalled referrals — click into a filtered view"
            action={<CcDigestButton />} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {[
              { key: "waiting", count: s.external_referrals.waiting, label: "Waiting on partner", to: "/external-referrals?bucket=waiting" },
              { key: "accepted_today", count: s.external_referrals.accepted_today, label: "Accepted today", to: "/external-referrals?bucket=accepted_today" },
              { key: "declined_today", count: s.external_referrals.declined_today, label: "Declined today", to: "/external-referrals?bucket=declined_today" },
              { key: "follow_up", count: s.external_referrals.follow_up, label: "Follow-up overdue", to: "/external-referrals?bucket=follow_up" },
              { key: "quote_pending", count: s.external_referrals.quote_pending, label: "Quote / estimate pending", to: "/external-referrals?bucket=quote_pending" },
              { key: "quote_uploaded", count: s.external_referrals.quote_uploaded, label: "Quote / estimate uploaded", to: "/external-referrals?bucket=quote_uploaded" },
              { key: "tour_pending", count: s.external_referrals.tour_pending, label: "Tour / appointment pending", to: "/external-referrals?bucket=tour_pending" },
              { key: "outcome_pending", count: s.external_referrals.outcome_pending, label: "Outcome confirmation pending", to: "/external-referrals?bucket=outcome_pending" },
              { key: "stalled", count: s.external_referrals.stalled, label: "Stalled (7+ days)", to: "/external-referrals?bucket=stalled" },
              { key: "sla_nudge", count: s.external_referrals.sla_nudge, label: "Awaiting partner response", to: "/external-referrals?bucket=sla_nudge" },
              { key: "new_messages", count: s.external_referrals.new_messages, label: "New partner messages", to: "/external-referrals?bucket=new_messages" },
              { key: "docs_review", count: s.external_referrals.docs_review, label: "Documents needing review", to: "/reports/external-referrals" },
              { key: "meetings_upcoming", count: s.external_referrals.meetings_upcoming, label: "Meetings upcoming", to: "/reports/external-referrals" },
              { key: "open", count: s.external_referrals.total_open, label: "All open referrals", to: "/external-referrals" },
            ].map((c) => (
              <button key={c.key} data-testid={`cc-extref-${c.key}`} onClick={() => navigate(c.to)}
                className="text-left rounded-xl border border-[#EEF3F6] p-3 hover:border-[#1C4358]/40 transition-colors">
                <div className="text-2xl font-bold text-[#1C4358] tabular-nums">{c.count ?? 0}</div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {/* PROMPT 19C — Communication alerts (escalations, routing rules, SMS gate) */}
      {s.communication_alerts?.available && (
        <SectionCard testid="section-communication-alerts">
          <SectionHead icon={Icons.BellRing} title="Communication alerts" tone="red"
            hint="Escalations, routing coverage & SMS status — route items to the right person"
            action={<div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate("/notifications/escalation-health")}
                className="border-[#D6E0E7] text-[#1C4358]" data-testid="cc-comm-escalation-health">Escalation health</Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/settings/notification-routing")}
                className="border-[#D6E0E7] text-[#1C4358]" data-testid="cc-comm-routing-open">Notification routing</Button>
            </div>} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {[
              { key: "open_escalations", count: s.communication_alerts.open_escalations, label: "Open escalations", to: "/settings/notification-routing", tone: "#9C1E22" },
              { key: "urgent_escalations", count: s.communication_alerts.urgent_escalations, label: "Urgent escalations", to: "/settings/notification-routing", tone: "#9C1E22" },
              { key: "sla_open", count: s.communication_alerts.sla_open, label: "SLA escalations open", to: "/notifications", tone: "#9C1E22" },
              { key: "urgent_past_threshold", count: s.communication_alerts.urgent_past_threshold, label: "Urgent items past threshold", to: "/notifications", tone: "#B7791F" },
              { key: "central_fallback", count: s.communication_alerts.central_fallback, label: "Routed to central admin", to: "/settings/notification-routing", tone: "#B7791F" },
              { key: "failed_deliveries", count: s.communication_alerts.failed_deliveries, label: "Failed deliveries", to: "/settings/communications", tone: "#B7791F" },
              { key: "resolved_today", count: s.communication_alerts.resolved_today, label: "Resolved today", to: "/settings/notification-routing", tone: "#2F7D5A" },
              { key: "rules_active", count: s.communication_alerts.rules_active, label: "Active routing rules", to: "/settings/notification-routing", tone: "#1C4358" },
            ].map((c) => (
              <button key={c.key} data-testid={`cc-comm-${c.key}`} onClick={() => navigate(c.to)}
                className="text-left rounded-xl border border-[#EEF3F6] p-3 hover:border-[#9C1E22]/40 transition-colors">
                <div className="text-2xl font-bold tabular-nums" style={{ color: c.tone }}>{c.count ?? 0}</div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
          <div className="mt-2.5 text-[11.5px] flex items-center gap-1.5" style={{ color: s.communication_alerts.sms_enabled ? "#2F7D5A" : "#B7791F" }} data-testid="cc-comm-sms-status">
            <Icons.Smartphone size={13} /> SMS notifications are {s.communication_alerts.sms_enabled ? "enabled" : "disabled (provider, compliance & opt-in required)"}.
          </div>
        </SectionCard>
      )}

      {/* PROMPT 14G — Territory rollup (aggregate-only, links to /reports/territories) */}
      {s.territory_rollup?.available && (
        <SectionCard testid="section-territory-rollup">
          <SectionHead icon={Icons.MapPinned} title="Territory rollup" tone="navy"
            hint="Leads, consultations, local-page & partner activity across your territories — aggregate only"
            action={<Button size="sm" variant="outline" onClick={() => navigate("/reports/territories")}
              className="border-[#D6E0E7] text-[#1C4358]" data-testid="cc-territory-open">Open Territory Reports</Button>} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5">
            {[
              { key: "leads_today", count: s.territory_rollup.leads_today, label: "Leads today" },
              { key: "leads_week", count: s.territory_rollup.leads_week, label: "Leads this week" },
              { key: "consultations", count: s.territory_rollup.consultations_requested, label: "Consultations requested" },
              { key: "local_page_leads", count: s.territory_rollup.local_page_leads, label: "Local page leads (7d)" },
              { key: "ext_awaiting", count: s.territory_rollup.external_awaiting_response, label: "Partner awaiting response" },
              { key: "docs_review", count: s.territory_rollup.documents_needing_review, label: "Documents needing review" },
              { key: "stale_docs", count: s.territory_rollup.stale_documents, label: "Stale documents" },
            ].map((c) => (
              <button key={c.key} data-testid={`cc-territory-${c.key}`} onClick={() => navigate("/reports/territories")}
                className="text-left rounded-xl border border-[#EEF3F6] p-3 hover:border-[#1C4358]/40 transition-colors">
                <div className="text-2xl font-bold text-[#1C4358] tabular-nums">{c.count ?? 0}</div>
                <div className="text-[12px] text-muted-foreground leading-tight mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
          {s.territory_rollup.top_territory_needing_attention && (
            <button onClick={() => navigate(`/reports/territories/${s.territory_rollup.top_territory_needing_attention.franchise_id}`)}
              className="mt-3 w-full text-left rounded-lg border border-[#9C1E22]/20 bg-[rgba(156,30,34,0.04)] px-3 py-2.5 flex items-center gap-2"
              data-testid="cc-territory-top-attention">
              <Icons.AlertTriangle size={15} className="text-[#9C1E22] shrink-0" />
              <span className="text-[13px] text-[#252221]">
                <b>{s.territory_rollup.top_territory_needing_attention.name}</b> needs attention —
                {" "}{s.territory_rollup.top_territory_needing_attention.stale_documents} stale doc(s),
                {" "}{s.territory_rollup.top_territory_needing_attention.overdue_referrals} stalled referral(s).
              </span>
            </button>
          )}
        </SectionCard>
      )}

      {/* PROMPT 4A — Lead Source Analytics (public-website lead performance) */}
      <LeadSourceAnalytics filters={filters} />

      {/* PROMPT 7A — Conversations & Conversion (live chat + intake funnel) */}
      <ConversationsConversion filters={filters} />

      {/* AI Suggestions — rule-based, always available (fallback) */}
      <SectionCard testid="section-ai-suggestions">
        <SectionHead icon={Sparkles} title="Quick Actions" tone="amber" hint="Rule-based next best actions — always available, even if the AI briefing is off" />
        <div className="space-y-2">
          {(s.ai_suggestions?.items || []).map((sug, i) => {
            const SIcon = Icons[sug.icon] || Sparkles;
            const t = TONES[sug.tone] || TONES.navy;
            return (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-[#EEF3F6] px-3 py-2.5" data-testid={`ai-suggestion-${i}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: t.chip }}>
                    <SIcon size={15} style={{ color: t.color }} />
                  </div>
                  <span className="text-sm text-[#252221]">{sug.text}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(sug.link)}
                  className="shrink-0 border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]" data-testid={`ai-suggestion-action-${i}`}>
                  {sug.action} <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Territory Performance + Revenue / Money Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard testid="section-territory-performance" className="lg:col-span-2">
          <SectionHead icon={MapIcon} title="Territory Performance" count={s.territory_performance?.count} tone="navy"
            hint="Leads, active cases and won revenue by metro / region" />
          {(s.territory_performance?.items || []).length === 0 ? (
            <EmptyRow text="No territory data in the current scope." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="territory-table">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-[#1C4358]/70 border-b border-[#EEF3F6]">
                    <th className="text-left font-semibold py-2">Territory</th>
                    <th className="text-right font-semibold py-2">Branches</th>
                    <th className="text-right font-semibold py-2">Leads</th>
                    <th className="text-right font-semibold py-2">Active Cases</th>
                    <th className="text-right font-semibold py-2">Revenue Won</th>
                  </tr>
                </thead>
                <tbody>
                  {s.territory_performance.items.map((t) => (
                    <tr key={t.id} className="border-b border-[#F0F4F7] hover:bg-[rgba(155,185,206,0.08)]" data-testid="territory-row">
                      <td className="py-2.5 font-medium text-[#252221]">{t.name}</td>
                      <td className="py-2.5 text-right tabular-nums">{t.branches}</td>
                      <td className="py-2.5 text-right tabular-nums">{t.leads}</td>
                      <td className="py-2.5 text-right tabular-nums">{t.active_cases}</td>
                      <td className="py-2.5 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{formatCurrency(t.revenue_won)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard testid="section-revenue-flow">
          <SectionHead icon={Wallet} title="Revenue / Money Flow" tone="green" hint="Pipeline, earnings and pending payouts" />
          <div className="space-y-2.5">
            {[
              { label: "Expected", value: s.revenue_flow?.expected, icon: TrendingUp, tone: "navy" },
              { label: "Actual (won)", value: s.revenue_flow?.actual, icon: DollarSign, tone: "green" },
              { label: "Collected (paid)", value: s.revenue_flow?.paid, icon: Wallet, tone: "sky" },
              { label: "Pending payment", value: s.revenue_flow?.pending_payment, icon: AlarmClock, tone: "amber" },
            ].map((r) => {
              const t = TONES[r.tone];
              return (
                <div key={r.label} className="flex items-center justify-between rounded-lg border border-[#EEF3F6] px-3 py-2" data-testid={`revflow-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="flex items-center gap-2 text-sm text-[#252221]">
                    <r.icon size={15} style={{ color: t.color }} /> {r.label}
                  </span>
                  <span className="text-sm font-bold tabular-nums text-[#252221]">{formatCurrency(r.value || 0)}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Franchise / Sub-Site Manager */}
      <SectionCard testid="section-franchises">
        <SectionHead icon={Building2} title="Franchise / Sub-Site Manager" count={s.franchises?.count} tone="navy" badge="local"
          hint="Local sub-sites. Local admins edit phone, email and SEO; brand stays global." />
        {(s.franchises?.items || []).length === 0 ? (
          <EmptyRow text="No franchises / sub-sites in the current scope." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {s.franchises.items.map((f) => (
              <div key={f.id} className="rounded-xl border border-[#E6EDF2] p-4" data-testid="franchise-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-[#252221]">{f.name}</div>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(47,125,90,0.12)", color: GREEN }}>{f.status}</span>
                </div>
                {f.subdomain && <div className="text-xs text-muted-foreground mt-0.5">/{f.subdomain}</div>}
                <div className="mt-3 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Owner</span><span className="text-[#252221] font-medium">{f.owner_name || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-muted-foreground">Phone <ControlBadge kind="local" /></span>
                    <span className="text-[#252221]">{f.local_phone || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-muted-foreground">SEO title <ControlBadge kind="local" /></span>
                    <span className="text-[#252221] truncate max-w-[140px]" title={f.local_seo_title}>{f.local_seo_title || "—"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Lists: Consultations, Partner Referrals, Documents review */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SectionCard testid="section-consultations">
          <SectionHead icon={CalendarCheck2} title="Consultations Booked" count={s.consultations_booked?.count} tone="green" />
          {(s.consultations_booked?.items || []).length === 0 ? <EmptyRow text="No upcoming consultations." /> : (
            <div className="divide-y divide-[#F0F4F7]">
              {s.consultations_booked.items.map((c) => (
                <RowLink key={c.id} testid="consult-row" onClick={() => navigate("/consultations")}
                  primary={c.family_name || "Consultation"} secondary={formatDateTime(c.scheduled_at)} />
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard testid="section-partner-referrals">
          <SectionHead icon={Handshake} title="Partner Referrals" count={s.partner_referrals?.count} tone="khaki" badge="hybrid"
            hint="Listings drafted locally, approved centrally" />
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.entries(s.partner_referrals?.by_status || {}).map(([k, v]) => (
              <span key={k} className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(196,200,155,0.30)", color: KHAKI }}>{prettify(k)}: {v}</span>
            ))}
          </div>
          {(s.partner_referrals?.items || []).length === 0 ? <EmptyRow text="No referrals yet." /> : (
            <div className="divide-y divide-[#F0F4F7]">
              {s.partner_referrals.items.map((r) => (
                <RowLink key={r.id} testid="referral-row" onClick={() => navigate("/referrals")}
                  primary={prettify(r.referral_type || r.status || "Referral")} secondary={`Sent ${formatDate(r.sent_date || r.created_at)}`} />
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard testid="section-documents-review">
          <SectionHead icon={FileText} title="Documents Needing Review" count={s.documents_review?.count} tone="sky" />
          {(s.documents_review?.items || []).length === 0 ? <EmptyRow text="No legal/contract documents awaiting review." /> : (
            <div className="divide-y divide-[#F0F4F7]">
              {s.documents_review.items.map((d) => (
                <RowLink key={d.id} testid="document-row" onClick={() => navigate("/documents")}
                  primary={d.file_name || d.original_filename || "Document"} secondary={`${prettify(d.category || "other")} · ${formatDate(d.created_at)}`} />
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Shell/coming-soon: Live Chat, SEO Health, Q&A Approval + Reports + Help */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SectionCard testid="section-live-chat" className="bg-[rgba(155,185,206,0.06)]">
          <SectionHead icon={MessageSquare} title="Live Chat Requests" tone="sky" hint="Chats waiting for a team member — jump into the inbox to take over" />
          <div className="flex items-center gap-4">
            <div><div className="text-2xl font-bold font-display" style={{ color: SKY }}>{s.live_chat?.waiting ?? 0}</div><div className="text-[11px] text-muted-foreground">Waiting</div></div>
            <div><div className="text-2xl font-bold font-display text-[#1C4358]">{s.live_chat?.active ?? 0}</div><div className="text-[11px] text-muted-foreground">Active</div></div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{s.live_chat?.note}</p>
          <Button variant="outline" size="sm" data-testid="live-chat-open" onClick={() => navigate("/chat")}
            className="mt-3 border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
            Open Live Chat inbox <ArrowRight size={14} className="ml-1" />
          </Button>
        </SectionCard>

        <SectionCard testid="section-seo-health" className="bg-[rgba(183,121,31,0.05)]">
          <SectionHead icon={Search} title="SEO Health" tone="amber" badge="hybrid" hint="Keywords drafted locally, approved by Super Admin" />
          <p className="text-sm text-muted-foreground">{s.seo_health?.note}</p>
          <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Not yet available</div>
        </SectionCard>

        <SectionCard testid="section-qa-queue" className="bg-[rgba(47,125,90,0.05)]">
          <SectionHead icon={HelpCircle} title="Q&A Approval Queue" count={s.qa_queue?.count} tone="green" badge="hybrid"
            hint="Local drafts require Super Admin approval" />
          <p className="text-sm text-muted-foreground">{s.qa_queue?.note}</p>
          <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Not yet available</div>
        </SectionCard>

        <SectionCard testid="section-reports">
          <SectionHead icon={BarChart3} title="Reports" tone="navy" hint="Deep-dive reports and exports" />
          <Button variant="outline" size="sm" data-testid="reports-open" onClick={() => navigate("/reports")}
            className="border-[#D6E0E7] text-[#1C4358] hover:bg-[rgba(155,185,206,0.14)]">
            Open Reports <ArrowRight size={14} className="ml-1" />
          </Button>
        </SectionCard>

        <SectionCard testid="section-help" className="lg:col-span-2">
          <SectionHead icon={LifeBuoy} title="How-To / Help" tone="khaki" hint="Quick guidance to get things done" />
          <ul className="text-sm text-[#252221] space-y-1.5 list-disc pl-5">
            <li>Use the <strong>filters</strong> above to focus on a territory, city, service lane, person or source.</li>
            <li><strong>AI Suggestions</strong> tells you the highest-impact next action — start there each morning.</li>
            <li>Badges show who controls a setting: <ControlBadge kind="global" /> everywhere · <ControlBadge kind="local" /> this sub-site · <ControlBadge kind="hybrid" /> local draft + approval.</li>
            <li>Click any KPI card to jump straight to that list.</li>
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}
